import { useState } from 'react';

interface QuotaConfig {
  maxTransactions: number;
  maxApiCalls: number;
  maxUsers: number;
}

interface Step4QuotasProps {
  pricingTier: string;
  quotas: QuotaConfig;
  onChange: (quotas: QuotaConfig) => void;
}

const DEFAULT_QUOTAS: Record<string, QuotaConfig> = {
  starter: {
    maxTransactions: 5000,
    maxApiCalls: 50000,
    maxUsers: 25,
  },
  growth: {
    maxTransactions: 10000,
    maxApiCalls: 100000,
    maxUsers: 50,
  },
  enterprise: {
    maxTransactions: 1000000,
    maxApiCalls: 10000000,
    maxUsers: 1000,
  },
};

export function Step4Quotas({ pricingTier, quotas, onChange }: Step4QuotasProps) {
  const [customizeQuotas, setCustomizeQuotas] = useState(false);
  const defaultQuotas = DEFAULT_QUOTAS[pricingTier] || DEFAULT_QUOTAS.starter;

  const handleToggleCustomize = () => {
    if (!customizeQuotas) {
      // Switching to customize mode - keep current quotas
      setCustomizeQuotas(true);
    } else {
      // Switching back to defaults
      setCustomizeQuotas(false);
      onChange(defaultQuotas);
    }
  };

  const handleQuotaChange = (field: keyof QuotaConfig, value: string) => {
    const numValue = parseInt(value, 10);
    if (!isNaN(numValue) && numValue >= 0) {
      onChange({
        ...quotas,
        [field]: numValue,
      });
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Set Custom Quotas</h2>
        <p className="mt-2 text-sm text-gray-600">
          Override default limits for this platform. This is optional.
        </p>
      </div>

      {/* Default quotas info */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="font-semibold text-blue-900 mb-2">
          Your {pricingTier.charAt(0).toUpperCase() + pricingTier.slice(1)} plan includes:
        </h3>
        <ul className="space-y-1 text-sm text-blue-800">
          <li>• {defaultQuotas.maxTransactions.toLocaleString()} transactions/month</li>
          <li>• {defaultQuotas.maxApiCalls.toLocaleString()} API calls/month</li>
          <li>• {defaultQuotas.maxUsers.toLocaleString()} user accounts</li>
        </ul>
      </div>

      {/* Toggle customize */}
      <div className="flex items-center gap-3">
        <label className="flex items-center cursor-pointer group">
          <input
            type="checkbox"
            checked={customizeQuotas}
            onChange={handleToggleCustomize}
            className="w-[18px] h-[18px] rounded border-2 border-gray-300 text-blue-600 focus:ring-2 focus:ring-blue-500 focus:ring-offset-0 cursor-pointer"
          />
          <span className="ml-2 text-sm font-medium text-gray-900 group-hover:text-gray-700">
            Override default quotas
          </span>
        </label>
      </div>

      {/* Custom quota inputs */}
      {customizeQuotas && (
        <div className="space-y-4 mt-6">
          <div>
            <label
              htmlFor="max-transactions"
              className="block text-sm font-semibold text-gray-900 mb-2"
            >
              Monthly Transaction Limit
            </label>
            <input
              type="number"
              id="max-transactions"
              value={quotas.maxTransactions}
              onChange={(e) => handleQuotaChange('maxTransactions', e.target.value)}
              min="0"
              step="1000"
              className="w-full px-3 py-2.5 border border-gray-300 rounded-md font-mono text-right focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <p className="mt-1 text-xs text-gray-500">
              Number of transactions allowed per month
            </p>
          </div>

          <div>
            <label
              htmlFor="max-api-calls"
              className="block text-sm font-semibold text-gray-900 mb-2"
            >
              Monthly API Call Limit
            </label>
            <input
              type="number"
              id="max-api-calls"
              value={quotas.maxApiCalls}
              onChange={(e) => handleQuotaChange('maxApiCalls', e.target.value)}
              min="0"
              step="10000"
              className="w-full px-3 py-2.5 border border-gray-300 rounded-md font-mono text-right focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <p className="mt-1 text-xs text-gray-500">
              Number of API calls allowed per month
            </p>
          </div>

          <div>
            <label
              htmlFor="max-users"
              className="block text-sm font-semibold text-gray-900 mb-2"
            >
              Maximum User Accounts
            </label>
            <input
              type="number"
              id="max-users"
              value={quotas.maxUsers}
              onChange={(e) => handleQuotaChange('maxUsers', e.target.value)}
              min="0"
              step="5"
              className="w-full px-3 py-2.5 border border-gray-300 rounded-md font-mono text-right focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <p className="mt-1 text-xs text-gray-500">
              Maximum number of user accounts allowed
            </p>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div className="flex gap-3">
              <svg
                className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
              <div className="text-sm text-amber-900">
                <strong>Custom quotas override plan defaults</strong>
                <p className="mt-1 text-amber-800">
                  Setting custom quotas will override the default limits for this platform.
                  Ensure the values align with your billing and capacity planning.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
